import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { errText, papi } from "../portalApi";
import { rs } from "../perf/PerfDashboard";
import { CodeFilter } from "./LeaderOverview";
import { ClientList, FilterBar, applyFilter } from "../crm/ClientList";
import { ClientProfile } from "../crm/ClientProfile";
import { CrmKpis } from "../crm/PortalClients";

const ST = [["", "All"], ["pending", "Received"], ["in_process", "In process"], ["completed", "Completed"], ["cancelled", "Cancelled"], ["deleted", "Deleted"]];
const TONE = { pending: "bg-slate-100 text-slate-600", in_process: "bg-sky-100 text-sky-700", completed: "bg-emerald-100 text-emerald-700", cancelled: "bg-rose-100 text-rose-700", deleted: "bg-slate-200 text-slate-500" };

export const LeaderParcels = ({ me, initialCode = "" }) => {
  const [code, setCode] = useState(initialCode);
  const [days, setDays] = useState(30);
  const [st, setSt] = useState("");
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    const t = setTimeout(() => papi("get", `/leader/parcels?days=${days}&code=${code}&status=${st}&q=${encodeURIComponent(q)}`).then(setD).catch((e) => toast.error(errText(e))), 250);
    return () => clearTimeout(t);
  }, [code, days, st, q]);
  return (
    <div className="space-y-3" data-testid="leader-parcels">
      <CodeFilter codes={me.lead_codes} code={code} setCode={setCode} days={days} setDays={setDays} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">{ST.map(([k, l]) => <button key={k || "all"} onClick={() => setSt(k)} className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${st === k ? "bg-teal-600 text-white" : "border bg-white"}`} data-testid={`leader-status-${k || "all"}`}>{l}</button>)}</div>
        <div className="relative ml-auto w-64"><Search className="absolute left-2 top-2 h-4 w-4 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Customer, tracking, address" className="w-full rounded-lg border py-1.5 pl-8 pr-2 text-sm" data-testid="leader-parcel-search" /></div>
      </div>
      {!d ? <Loader2 className="mx-auto my-10 h-5 w-5 animate-spin text-slate-400" /> : (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <p className="px-3 pt-2 text-xs text-slate-500" data-testid="leader-parcel-count">{d.count} parcel(s) · view only</p>
          <table className="w-full min-w-[820px] text-sm"><thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500"><tr>{["Date", "Code", "Customer", "Products", "Mode / Tracking", "Status", "Total", "Paid"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
            <tbody>{d.parcels.map((g) => (
              <tr key={`${g.code}-${g.key}`} className="border-t align-top" data-testid={`leader-parcel-${g.key}`}>
                <td className="whitespace-nowrap px-3 py-2">{g.date}</td><td className="px-3 py-2 font-mono text-xs">{g.code}</td>
                <td className="px-3 py-2"><b>{g.customer_name}</b><div className="max-w-[200px] truncate text-xs text-slate-400">{g.address}</div></td>
                <td className="px-3 py-2 text-xs">{(g.items || []).map((i) => `${i.medicine} × ${i.quantity}`).join(", ")}</td>
                <td className="px-3 py-2 text-xs">{g.mode}<div className="font-mono text-slate-500">{g.tracking}</div></td>
                <td className="px-3 py-2"><span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${TONE[g.sync_status] || ""}`}>{g.status_label}</span>{g.reason && <div className="text-[11px] text-rose-600">{g.reason}</div>}</td>
                <td className="px-3 py-2">{g.grand_total ? rs(g.grand_total) : "—"}</td><td className="px-3 py-2">{g.payment_amount ? rs(g.payment_amount) : "—"}</td></tr>))}</tbody></table>
        </div>)}
    </div>
  );
};

export const LeaderClients = ({ me }) => {
  const [code, setCode] = useState("");
  const [d, setD] = useState(null);
  const [f, setF] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  useEffect(() => { setD(null); setSel(null); papi("get", `/leader/clients?code=${code}`).then(setD).catch((e) => toast.error(errText(e))); }, [code]);
  const rows = d ? applyFilter(d.clients, f, q, d.today) : [];
  const c = d?.clients.find((x) => x.key === sel);
  return (
    <div className="space-y-3" data-testid="leader-clients">
      <CodeFilter codes={me.lead_codes} code={code} setCode={setCode} />
      {!d ? <Loader2 className="mx-auto my-10 h-5 w-5 animate-spin text-slate-400" /> : <>
        <CrmKpis k={d.kpis} />
        <div className="grid gap-3 md:grid-cols-[320px_1fr]">
          <div className={`space-y-2 ${c ? "hidden md:block" : ""}`}><FilterBar f={f} setF={setF} q={q} setQ={setQ} /><ClientList rows={rows} sel={sel} onPick={setSel} /></div>
          {c ? <div><p className="mb-1 text-xs text-slate-500">{c.codes.length ? `Code ${c.codes.join(", ")} · ` : ""}partner {c.owner} · view only</p><ClientProfile c={c} today={d.today} readOnly onBack={() => setSel(null)} /></div>
            : <p className="hidden rounded-xl border border-dashed p-8 text-center text-sm text-slate-400 md:block">Pick a client to see the profile.</p>}
        </div>
      </>}
    </div>
  );
};
